import { useCallback, useState } from 'react';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { PATH_DASHBOARD } from '../../routes/paths';
import {
  AdminErrorState,
  AdminLoadingState,
  AdminScreenLayout,
  AdminStatusChip,
} from '../../sections/admin/shared';

export const breadcrumbsFor = (title) => [
  { name: 'Admin', href: PATH_DASHBOARD.root },
  { name: title },
];

export function AdminRouteLayout({ title, breadcrumbTitle, children }) {
  return (
    <AdminScreenLayout title={title} breadcrumbs={breadcrumbsFor(breadcrumbTitle || title)}>
      {children}
    </AdminScreenLayout>
  );
}

AdminRouteLayout.propTypes = {
  title: PropTypes.string.isRequired,
  breadcrumbTitle: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export function StatCard({ title, value, caption, status }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h4">{value}</Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {caption}
            </Typography>
            {status ? <AdminStatusChip status={status} /> : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  caption: PropTypes.string.isRequired,
  status: PropTypes.string,
};

export function RequestState({ loading, error, children }) {
  if (loading) {
    return <AdminLoadingState rows={4} />;
  }

  if (error) {
    return <AdminErrorState title="Request failed" message={error} />;
  }

  return children;
}

RequestState.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export function useAsyncState(initialData = null) {
  const [state, setState] = useState({ loading: false, error: null, data: initialData });

  const setLoading = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
  }, []);

  const setData = useCallback((data) => {
    setState({ loading: false, error: null, data });
  }, []);

  const setError = useCallback((error) => {
    setState((prev) => ({ ...prev, loading: false, error }));
  }, []);

  return {
    state,
    setLoading,
    setData,
    setError,
  };
}
